import { useState, useEffect, useCallback, useMemo, useRef, startTransition } from "react";
import { useLocation } from "react-router-dom";
import {
  FileText, Plus, X, RefreshCw, CheckCircle2, Send, Clock, TrendingUp,
  Archive, BookOpen, AlertTriangle, Users, Download, FileSpreadsheet, Layers
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../contexts/AuthContext";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { daysUntil } from "../utils/format";
import { budgetSummary, budgetReadCancelled, createBudgetReads } from "../utils/budgetReads";
import { useSubTabs } from "../contexts/TabsContext";
import { openBudgetDoc, closeBudgetDoc, budgetDocTitle } from "../utils/tabsState";

// Sub-components
import { Modal } from "./budgets/SharedComponents";
import { opts, fmt } from "./budgets/constants";
import { BudgetCard, ConfirmDelete, TransitionModal, PdfOptionsModal, IniciarComputoModal, DuplicateModal } from "./budgets/BudgetDialogs";
import { BudgetForm } from "./budgets/BudgetForm";
import { BudgetDetail } from "./budgets/BudgetDetail";
import { PriceListManager } from "./budgets/PriceListManager";
import { BaseMadrePanel } from "./budgets/BaseMadrePanel";
import { AttachmentsModal } from "./budgets/AttachmentsModal";
import { PapeleraModal } from "./budgets/PapeleraModal";
import { ClienteFilterDropdown } from "./budgets/BudgetDialogs";

export default function Budgets() {
  const { user } = useAuth();
  const location = useLocation();
  const [budgets, setBudgets] = useState([]);
  const [stats, setStats] = useState({});
  const [projects, setProjects] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [obras, setObras] = useState([]);
  const [filterObraId, setFilterObraId] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("obra_id") || ""; }
    catch { return ""; }
  });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("todos");
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterMonth, setFilterMonth] = useState("");
  const [filterTipo, setFilterTipo] = useState(() => {
    const path = window.location.pathname;
    if (path.endsWith("/obra")) return "obra";
    if (path.endsWith("/proyecto")) return "proyecto";
    if (path.endsWith("/varios")) return "varios";
    return "";
  });
  const [filterClienteId, setFilterClienteId] = useState("");
  // Fix DI (2026-09-17): "favor agregar un BUSCADOR en el area de presupuestos,
  // donde tipear el nombre de obra y poder filtrar a traves de eso". Busca en el
  // nombre del presupuesto, en la obra, en el número y en el cliente: con 157
  // presupuestos, acordarse del número es más difícil que del nombre de la obra.
  const [busqueda, setBusqueda] = useState("");
  const [docs, setDocs] = useState([]);
  const [activeDocId, setActiveDocId] = useState(null);
  const [docSeq, setDocSeq] = useState(1);
  const [dirtyDocs, setDirtyDocs] = useState({});
  const docsRef = useRef(docs); docsRef.current = docs;
  const seqRef = useRef(docSeq); seqRef.current = docSeq;
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showPriceLists, setShowPriceLists] = useState(false);
  const [showBaseMadre, setShowBaseMadre] = useState(false); // Fix CC (2026-09-10)
  const [showPapelera, setShowPapelera] = useState(false);
  const [attachTarget, setAttachTarget] = useState(null);
  const [transitionTarget, setTransitionTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  // FIX 2 — Duplicar presupuesto
  const [duplicateTarget, setDuplicateTarget] = useState(null);

  const [showPdfOptions, setShowPdfOptions] = useState(false);
  const [pdfBudget, setPdfBudget] = useState(null);
  // A3: Modal especial para Iniciar Cómputo
  const [showIniciarComputo, setShowIniciarComputo] = useState(false);
  const [computoTarget, setComputoTarget] = useState(null);

  const canEdit    = ["Superadministrador", "Administrador", "Gerente Tecnico", "Ingeniero Residente", "Coordinador de Obra", "Compras"].includes(user?.role);
  const canAdvance = ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra", "Ingeniero Residente"].includes(user?.role);
  const canDelete  = ["Superadministrador", "Administrador", "Gerente Tecnico"].includes(user?.role);

  // La lista no necesita descargar ítems, revisiones ni adjuntos de todos los presupuestos.
  const [loadError, setLoadError] = useState("");
  const [detailLoading, setDetailLoading] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const reader = useMemo(() => createBudgetReads((url, config) => axios.get(url, config), API, opts), [user?._id, user?.role]);
  const readerRef = useRef(reader);
  readerRef.current = reader;
  const mountedRef = useRef(false);
  const listRequestRef = useRef(0);
  const statsRequestRef = useRef(0);
  const actionRequestRef = useRef(0);
  const isCurrent = useCallback(() => mountedRef.current && readerRef.current === reader, [reader]);

  useEffect(() => {
    mountedRef.current = true;
    setBudgets([]); setProjects([]); setClientes([]); setObras([]); setStats({});
    setDocs([]); setActiveDocId(null); setDirtyDocs({});
    setAttachTarget(null); setTransitionTarget(null); setRejectTarget(null); setDuplicateTarget(null);
    setDeleteTarget(null); setShowPdfOptions(false); setPdfBudget(null); setShowIniciarComputo(false); setComputoTarget(null);
    setDetailLoading(null);
    return () => {
      mountedRef.current = false;
      listRequestRef.current += 1; statsRequestRef.current += 1; actionRequestRef.current += 1;
      reader.cancelAll();
    };
  }, [reader]);

  const ensureReferences = useCallback(async (force = false) => {
    const data = await reader.references(force);
    if (isCurrent()) { setProjects(data.projects); setClientes(data.clientes); setObras(data.obras); }
    return data;
  }, [reader, isCurrent]);

  const fetchStats = useCallback(async () => {
    const request = ++statsRequestRef.current;
    if (isCurrent()) setStatsLoading(true);
    try {
      const data = await reader.stats();
      if (isCurrent() && request === statsRequestRef.current) setStats(data || {});
    } catch (error) {
      if (isCurrent() && request === statsRequestRef.current && !budgetReadCancelled(error)) toast.error("No se pudo actualizar el resumen de presupuestos");
    } finally { if (isCurrent() && request === statsRequestRef.current) setStatsLoading(false); }
  }, [reader, isCurrent]);

  const fetchBudgets = useCallback(async () => {
    const request = ++listRequestRef.current;
    setLoading(true); setLoadError("");
    try {
      const params = {};
      if (tab === "enviado" || tab === "aprobado") params.status = tab;
      if (filterYear) params.year = filterYear;
      if (filterMonth) params.month = filterMonth;
      if (filterTipo) params.tipo = filterTipo;
      if (filterClienteId) params.cliente_id = filterClienteId;
      const rows = await reader.list(params);
      if (isCurrent() && request === listRequestRef.current) setBudgets(rows);
    } catch (error) {
      if (isCurrent() && request === listRequestRef.current && !budgetReadCancelled(error)) {
        setLoadError("No se pudieron cargar los presupuestos. Volvé a intentarlo.");
      }
    } finally {
      if (isCurrent() && request === listRequestRef.current) setLoading(false);
    }
  }, [reader, isCurrent, tab, filterYear, filterMonth, filterTipo, filterClienteId]);

  useEffect(() => { fetchBudgets(); }, [fetchBudgets]);
  useEffect(() => {
    fetchStats();
    ensureReferences().catch(error => {
      if (isCurrent() && !budgetReadCancelled(error)) toast.error("No se pudieron cargar las opciones del formulario. Se reintentará al abrirlo.");
    });
  }, [fetchStats, ensureReferences, isCurrent]);

  // Guardados y refresco manual actualizan también los catálogos. Cambiar un filtro sólo recarga la lista.
  const fetchAll = useCallback(() => {
    fetchBudgets(); fetchStats();
    ensureReferences(true).catch(error => {
      if (isCurrent() && !budgetReadCancelled(error)) toast.error("No se pudieron actualizar las opciones del formulario");
    });
  }, [fetchBudgets, fetchStats, ensureReferences, isCurrent]);

  const cancelBudgetAction = () => {
    actionRequestRef.current += 1; reader.cancelDetail(); setDetailLoading(null);
  };
  const prepareBudgetAction = async (budget, open, needsReferences = false) => {
    const request = ++actionRequestRef.current;
    reader.cancelDetail();
    setDetailLoading(budget?._id || "nuevo");
    try {
      const [full] = await Promise.all([
        budget ? reader.detail(budget) : Promise.resolve(null),
        needsReferences ? ensureReferences() : Promise.resolve(),
      ]);
      if (isCurrent() && request === actionRequestRef.current) open(full);
    } catch (error) {
      if (isCurrent() && request === actionRequestRef.current && !budgetReadCancelled(error)) {
        toast.error(error.response?.data?.detail || "No se pudo abrir el presupuesto completo. Volvé a intentarlo.");
      }
    } finally {
      if (isCurrent() && request === actionRequestRef.current) setDetailLoading(null);
    }
  };
  const abrirDoc = (spec) => {
    const r = openBudgetDoc(docsRef.current, spec, seqRef.current);
    docsRef.current = r.docs; seqRef.current = r.seq;
    setDocs(r.docs); setActiveDocId(r.activeId); setDocSeq(r.seq);
  };
  const markDirty = (id) => setDirtyDocs(p => (p[id] ? p : { ...p, [id]: true }));
  // Fix IP: en transición, el re-render del padre no se confirma antes de que React lea el onChange de la celda (si no, la primera edición se pierde)
  const markDirtyDiferido = (id) => startTransition(() => markDirty(id));
  const cerrarDoc = (id, force = false) => {
    if (!force && dirtyDocs[id] && !window.confirm("Este presupuesto tiene cambios sin guardar. ¿Cerrar y descartarlos? Si cancelás, no se pierde nada.")) return;
    const r = closeBudgetDoc(docsRef.current, activeDocId, id);
    docsRef.current = r.docs;
    setDocs(r.docs); setActiveDocId(r.activeId);
    setDirtyDocs(p => { const n = { ...p }; delete n[id]; return n; });
  };
  useSubTabs(docs.map(d => ({ id: d.id, title: budgetDocTitle(d, docs), dirty: !!dirtyDocs[d.id] })), activeDocId, setActiveDocId, id => cerrarDoc(id));
  const hayCambios = Object.values(dirtyDocs).some(Boolean);
  useEffect(() => {
    if (!hayCambios) return undefined;
    const aviso = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", aviso);
    return () => window.removeEventListener("beforeunload", aviso);
  }, [hayCambios]);
  const openNewBudget = () => prepareBudgetAction(null, () => abrirDoc({ kind: "new" }), true);
  const openViewBudget = budget => prepareBudgetAction(budget, full => abrirDoc({ kind: "view", budget: full }));
  const openEditBudget = budget => prepareBudgetAction(budget, full => abrirDoc({ kind: "edit", budget: full }), true);
  const openAdditionalBudget = budget => prepareBudgetAction(budget, full => abrirDoc({ kind: "adicional", parent: full }), true);

  useEffect(() => {
    const path = location.pathname;
    if (path.endsWith("/obra"))            setFilterTipo("obra");
    else if (path.endsWith("/proyecto"))   setFilterTipo("proyecto");
    else if (path.endsWith("/varios"))     setFilterTipo("varios");
    else if (path === "/ingenieria/presupuestos") setFilterTipo("");
  }, [location.pathname]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/budgets/${deleteTarget._id}`, opts);
      toast.success("Presupuesto enviado a papelera");
      // A0 fix: limpiar viewBudget si coincide con el eliminado
      const abierto = docsRef.current.find(d => d.budget?._id === deleteTarget._id);
      if (abierto) cerrarDoc(abierto.id, true);
      setDeleteTarget(null);
      fetchAll();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleTransition = async (comment) => {
    try {
      await axios.post(`${API}/budgets/${transitionTarget._id}/transition`, { comment }, opts);
      toast.success("Estado avanzado correctamente");
      setTransitionTarget(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al avanzar estado"); }
  };

  // A3: Handler especial para Iniciar Cómputo (recepcion/borrador → computo)
  const handleIniciarComputo = async ({ deadlineComputo, alcance }) => {
    try {
      // 1. Transición del estado
      await axios.post(`${API}/budgets/${computoTarget._id}/transition`, { comment: "Cómputo iniciado" }, opts);
      // 2. Crear tarea de cómputo en Ingeniería.
      // Los presupuestos "De Proyecto" alimentan el módulo Proyectos; el resto, Cómputo.
      await axios.post(`${API}/computo-tasks`, {
        budget_id: computoTarget._id,
        budget_number: computoTarget.number,
        budget_nombre: computoTarget.nombre,
        cliente: computoTarget.cliente || "",
        alcance: alcance || computoTarget.nombre,
        deadline_computo: deadlineComputo,
        tipo: computoTarget.tipo === "proyecto" ? "proyecto" : "computo",
      }, opts);
      toast.success("Cómputo iniciado — Tarea creada en Módulo de Proyectos");
      setShowIniciarComputo(false);
      setComputoTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar cómputo");
    }
  };

  // A3: Interceptar transición cuando es Iniciar Cómputo
  // P1 V06: si ya hay cómputo disponible → ir directo a cotización (sin modal)
  const handleTransitionClick = (budget) => {
    const isIniciarComputo = budget.status === "recepcion" || budget.status === "borrador";
    if (isIniciarComputo) {
      if (budget.computo_disponible) {
        abrirDoc({ kind: "edit", budget });
        return;
      }
      setComputoTarget(budget);
      setShowIniciarComputo(true);
    } else {
      setTransitionTarget(budget);
    }
  };

  const handleReject = async (comment) => {
    if (!comment.trim()) { toast.error("El motivo de rechazo es obligatorio"); return; }
    try {
      await axios.post(`${API}/budgets/${rejectTarget._id}/reject`, { comment }, opts);
      toast.success("Presupuesto rechazado");
      setRejectTarget(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al rechazar"); }
  };

  // FIX 2/3 — Solo los presupuestos en estados ACTIVOS (no enviado/aprobado/rechazado)
  // cuentan como "próximos a vencer". Un budget ya enviado cumplió su flujo.
  const ACTIVE_DEADLINE_STATUSES = ["borrador", "recepcion", "computo", "cotizacion", "revision"];
  const upcomingToSend = (() => {
    const candidates = budgets.filter(b => ACTIVE_DEADLINE_STATUSES.includes(b.status) && b.deadline);
    candidates.sort((a, b) => (a.deadline || "").localeCompare(b.deadline || ""));
    return candidates.slice(0, 5);
  })();

  // BLOQUE 17 — daysToDeadline reemplazado por daysUntil() de utils/format.js (fuente
  // única, compara medianoche local vs medianoche local, sin el desfase UTC del patrón
  // anterior new Date(d) - new Date()).
  const daysToDeadline = daysUntil;

  const years = [2024, 2025, 2026, 2027];
  const meses = [
    { v: "1", l: "Enero" }, { v: "2", l: "Febrero" }, { v: "3", l: "Marzo" }, { v: "4", l: "Abril" },
    { v: "5", l: "Mayo" }, { v: "6", l: "Junio" }, { v: "7", l: "Julio" }, { v: "8", l: "Agosto" },
    { v: "9", l: "Septiembre" }, { v: "10", l: "Octubre" }, { v: "11", l: "Noviembre" }, { v: "12", l: "Diciembre" },
  ];

  const TAB_STATUS = {
    todos: null,
    "en-proceso": ["borrador", "recepcion", "computo", "cotizacion", "revision"],
    enviado:      ["enviado"],
    aprobado:     ["aprobado"],
  };

  const baseFilteredBudgets = tab === "todos" ? budgets : budgets.filter(b => TAB_STATUS[tab]?.includes(b.status));
  const obraFiltrada = obras.find(o => o._id === filterObraId);
  const porObraBudgets = filterObraId
    ? baseFilteredBudgets.filter(b =>
        b.obra_id === filterObraId ||
        (obraFiltrada && (b.obra || "").toLowerCase() === (obraFiltrada.obra || "").toLowerCase()))
    : baseFilteredBudgets;
  // Fix DI (2026-09-17): sin acentos y sin mayúsculas, para que "nobile"
  // encuentre "HOTEL NOBILE" y "jejui" encuentre "TORRE JEJUÍ".
  const _norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const _q = _norm(busqueda).trim();
  const filteredBudgets = _q
    ? porObraBudgets.filter(b =>
        _norm(b.nombre).includes(_q) || _norm(b.obra).includes(_q) ||
        _norm(b.number).includes(_q) || _norm(b.cliente).includes(_q))
    : porObraBudgets;
  const clienteFiltrado = clientes.find(c => c._id === filterClienteId);

  const clienteKpis = (() => {
    if (!filterClienteId) return null;
    const list = budgets.filter(b => b.cliente_id === filterClienteId);
    const sum = (arr) => arr.reduce((acc, b) => acc + (Number(b.total) || 0), 0);
    const aprobados = list.filter(b => b.status === "aprobado");
    const enviados  = list.filter(b => b.status === "enviado");
    const enProceso = list.filter(b => ["borrador", "recepcion", "computo", "cotizacion", "revision"].includes(b.status));
    return {
      total_count:       list.length,
      approved_count:    aprobados.length,
      approved_value:    sum(aprobados),
      sent_value:        sum(enviados),
      in_process_value:  sum(enProceso),
      total_value:       sum(list),
    };
  })();

  return (
    <div className="p-4 sm:p-6 space-y-5">
      {docs.map(doc => {
        const activo = doc.id === activeDocId;
        const titulo = budgetDocTitle(doc, docs);
        return (
          <div key={doc.id} hidden={!activo} inert={!activo} data-testid={`budget-doc-${doc.id}`}
            onChangeCapture={doc.kind === "view" ? undefined : () => markDirtyDiferido(doc.id)} onInputCapture={doc.kind === "view" ? undefined : () => markDirtyDiferido(doc.id)}>
            <div className="flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2.5 mb-3">
              <h2 className="text-sm font-bold text-gray-900 truncate" data-testid={`budget-doc-title-${doc.id}`}>
                {doc.kind === "view" ? "Presupuesto — " : doc.kind === "edit" ? "Editar Presupuesto — " : ""}{titulo}{dirtyDocs[doc.id] ? " (sin guardar)" : ""}
              </h2>
              <button type="button" onClick={() => cerrarDoc(doc.id)} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Cerrar presupuesto" data-testid={`budget-doc-close-${doc.id}`}><X size={16} className="text-gray-500" /></button>
            </div>
            {doc.kind === "view" ? (
              <div className="bg-white rounded-2xl border border-gray-100">
                <BudgetDetail budget={doc.budget} />
                <div className="px-6 pb-4 flex gap-3 border-t border-gray-100 pt-4">
                  <button onClick={() => { setPdfBudget(doc.budget); setShowPdfOptions(true); }}
                    className="flex items-center gap-2 bg-[#2E6DA4] text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-[#245A8A] transition-colors" data-testid="view-pdf-btn">
                    <Download size={15} /> PDF Formal
                  </button>
                  <a href={`${API}/budgets/${doc.budget._id}/export-excel`} className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors">
                    <FileSpreadsheet size={15} /> Excel
                  </a>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 overflow-y-auto flex flex-col h-[calc(100vh-12.5rem)] min-h-[28rem]">
                {/* Fix IH (2026-10-29): "ahora no puedo dar scroll al presupuesto ... es en análisis financiero" (cliente).
                    overflow-y-auto y no overflow-hidden: Análisis Financiero, Bocas, Mano de Obra y Catálogos no tienen scroll
                    propio y se desplazan dentro de este recuadro, como en el Modal de antes. La tabla de ítems sigue con alto
                    acotado y virtualizada (su contenedor tiene scroll propio). */}
                {/* Fix IE (2026-10-29): "no logro abrir el presupuesto de LINK CENTER" / "ahora abrió pero es hiper lento" (cliente).
                    BudgetForm virtualiza la tabla de ítems (useVirtualizer sobre items-scroll-container, flex-1 + overflow-y-auto)
                    y solo funciona si este contenedor tiene ALTO DEFINIDO, como lo tenía el Modal fullscreen antes de las pestañas.
                    Sin alto, el contenedor crecía al alto de toda la lista y se montaban las 2.375 filas a la vez (cuelgue / sin memoria).
                    Solo cambia el alto del contenedor: datos, cálculos, totales y permisos quedan igual. */}
                <BudgetForm
                  budget={doc.budget}
                  parentBudget={doc.parent}
                  projects={projects}
                  clientes={clientes}
                  obras={obras}
                  onSave={() => { cerrarDoc(doc.id, true); fetchAll(); }}
                  onClose={() => cerrarDoc(doc.id)}
                  onBudgetUpdated={(updated) => {
                    if (!updated || !updated._id) return;
                    setBudgets(prev => prev.map(b => b._id === updated._id ? budgetSummary(updated, true) : b));
                    setDocs(prev => prev.map(d => (d.id === doc.id ? { ...d, budget: updated } : d)));
                    setDirtyDocs(p => ({ ...p, [doc.id]: false }));
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
      <div hidden={!!activeDocId} className="space-y-5" data-testid="budgets-list-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Presupuestos</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {clienteFiltrado
              ? <span className="font-bold text-[#2E6DA4]">{clienteFiltrado.cliente} ({clienteFiltrado.codigo})</span>
              : "Gestión de presupuestos — CEN Ingeniería E.A.S."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowPapelera(true)} className="flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 px-3 py-2 rounded-xl text-sm font-semibold transition-colors" data-testid="papelera-btn">
            <Archive size={15} /> Papelera
          </button>
          <button onClick={() => setShowPriceLists(true)} className="flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 px-3 py-2 rounded-xl text-sm font-semibold transition-colors" data-testid="price-lists-btn">
            <BookOpen size={15} /> Listas de Precios
          </button>
          {/* Fix CC (2026-09-10): la base madre de Bocas / M.O. / Catálogos. */}
          <button onClick={() => setShowBaseMadre(true)} className="flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 px-3 py-2 rounded-xl text-sm font-semibold transition-colors" data-testid="base-madre-btn">
            <Layers size={15} /> Base Madre
          </button>
          <button onClick={fetchAll} className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100"><RefreshCw size={16} /></button>
          {canEdit && (
            <button onClick={openNewBudget}
              className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors shadow-sm"
              data-testid="new-budget-btn">
              <Plus size={15} /> Nuevo Presupuesto
            </button>
          )}
        </div>
      </div>

      {/* Panel por cliente */}
      {clienteFiltrado && clienteKpis && (
        <div className="bg-gradient-to-r from-[#1A4B7A] via-[#2E6DA4] to-[#3B82F6] rounded-2xl p-6 shadow-lg text-white" data-testid="cliente-kpi-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <Users size={22} className="text-white" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/70">Cliente seleccionado</p>
                <h2 className="text-xl font-bold mt-0.5">{clienteFiltrado.cliente}</h2>
                <div className="flex items-center gap-2 mt-1 text-xs text-white/80">
                  <span className="bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded font-bold">{clienteFiltrado.codigo}</span>
                  <span>·</span>
                  <span>{clienteKpis.total_count} presupuesto{clienteKpis.total_count !== 1 ? "s" : ""}</span>
                </div>
              </div>
            </div>
            <button onClick={() => setFilterClienteId("")}
              className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors"
              data-testid="clear-cliente-btn">
              <X size={12} /> Quitar filtro
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
            <div className="sm:col-span-3 bg-white rounded-xl p-5 shadow-md" data-testid="kpi-aprobado-cliente">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 size={14} className="text-emerald-600" />
                <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Valor Total Aprobado</p>
              </div>
              <p className="text-3xl sm:text-4xl font-bold text-emerald-700 tabular-nums">{fmt(clienteKpis.approved_value)}</p>
              <p className="text-xs text-gray-500 mt-1">
                {clienteKpis.approved_count} presupuesto{clienteKpis.approved_count !== 1 ? "s" : ""} aprobado{clienteKpis.approved_count !== 1 ? "s" : ""} · IVA incluido
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-3" data-testid="kpi-enviado-cliente">
              <div className="flex items-center gap-1.5 mb-1"><Send size={11} className="text-blue-200" /><p className="text-[10px] uppercase tracking-wider font-bold text-blue-100">Enviados</p></div>
              <p className="text-base font-bold text-white tabular-nums">{fmt(clienteKpis.sent_value)}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-3" data-testid="kpi-proceso-cliente">
              <div className="flex items-center gap-1.5 mb-1"><Clock size={11} className="text-amber-200" /><p className="text-[10px] uppercase tracking-wider font-bold text-amber-100">En Proceso</p></div>
              <p className="text-base font-bold text-white tabular-nums">{fmt(clienteKpis.in_process_value)}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-3" data-testid="kpi-total-cliente">
              <div className="flex items-center gap-1.5 mb-1"><TrendingUp size={11} className="text-white/80" /><p className="text-[10px] uppercase tracking-wider font-bold text-white/70">Total Cotizado</p></div>
              <p className="text-base font-bold text-white tabular-nums">{fmt(clienteKpis.total_value)}</p>
            </div>
          </div>
        </div>
      )}

      {/* FIX 3 — Top 5 próximos presupuestos a vencer (solo estados activos) */}
      {upcomingToSend.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-2xl p-4 shadow-sm" data-testid="upcoming-budgets-alert">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-xl bg-amber-200 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={18} className="text-amber-700" />
            </div>
            <p className="text-xs font-bold text-amber-900 uppercase tracking-wide">
              Próximos presupuestos a vencer ({upcomingToSend.length})
            </p>
          </div>
          <div className="space-y-2">
            {upcomingToSend.map((b) => {
              const d = daysToDeadline(b.deadline);
              const isOverdue = d !== null && d < 0;
              const isToday = d === 0;
              const isUrgent = d !== null && d > 0 && d <= 3;
              let label, labelColor;
              if (d === null) { label = "—"; labelColor = "text-gray-500"; }
              else if (d < 0) { label = `Vencido hace ${Math.abs(d)} día(s)`; labelColor = "text-red-700"; }
              else if (d === 0) { label = "Vence hoy"; labelColor = "text-red-700"; }
              else if (d <= 3) { label = `Vence en ${d} día(s)`; labelColor = "text-orange-600"; }
              else { label = `Vence en ${d} día(s)`; labelColor = "text-gray-500"; }
              return (
                <div
                  key={b._id}
                  className={`flex items-center gap-3 bg-white rounded-xl px-3 py-2 border ${isOverdue || isToday ? "border-red-300" : isUrgent ? "border-orange-200" : "border-gray-100"} shadow-sm`}
                  data-testid={`upcoming-budget-row-${b._id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm truncate" title={b.nombre}>{b.nombre}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-600 flex-wrap mt-0.5">
                      <span className="font-mono text-[#1A4B7A] font-bold">{b.number}</span>
                      <span className="text-gray-400">·</span>
                      <span className="truncate">{b.cliente || "—"}</span>
                      <span className="text-gray-400">·</span>
                      <span>{b.deadline}</span>
                    </div>
                  </div>
                  <span className={`text-xs font-bold whitespace-nowrap ${labelColor} ${isToday ? "animate-pulse" : ""}`} data-testid={`upcoming-days-${b._id}`}>
                    {label}
                  </span>
                  <button
                    onClick={() => openViewBudget(b)}
                    className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors flex-shrink-0"
                    data-testid={`upcoming-view-btn-${b._id}`}
                  >
                    Ver
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Total",       value: stats.total || 0,    sub: "presupuestos",  color: "text-gray-800",    bg: "bg-gray-50" },
          { label: "En Proceso",  value: stats.draft || 0,    sub: "en proceso",    color: "text-amber-700",   bg: "bg-amber-50" },
          { label: "Aprobados",   value: stats.approved || 0, sub: "confirmados",   color: "text-emerald-700", bg: "bg-emerald-50" },
          { label: "Valor Total de Presupuestos", value: null, sub: fmt(stats.total_value || 0),    color: "text-[#2E6DA4]",   bg: "bg-blue-50" },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-2xl border border-gray-100 p-4`} data-testid="stats-card">
            <p className="text-xs text-gray-500 font-medium">{s.label}</p>
            {statsLoading ? <p className="text-sm text-gray-500">Cargando…</p> : s.value !== null ? <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p> : <p className={`text-base font-bold ${s.color}`}>{s.sub}</p>}
            {s.value !== null && <p className="text-xs text-gray-400 mt-0.5">{s.sub}</p>}
          </div>
        ))}
      </div>

      {stats.approved_value > 0 && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-2.5 flex items-center gap-2">
          <CheckCircle2 size={14} className="text-emerald-600" />
          <p className="text-sm font-semibold text-emerald-700">Valor Total Aprobado: <span className="font-bold">{fmt(stats.approved_value)}</span></p>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center bg-gray-100 rounded-xl p-1 gap-0.5">
          {[["todos", "Todos"], ["en-proceso", "En Proceso"], ["enviado", "Enviados"], ["aprobado", "Aprobados"]].map(([val, label]) => (
            <button key={val} onClick={() => setTab(val)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${tab === val ? "bg-white text-[#2E6DA4] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              data-testid={`tab-${val}`}>
              {label}
            </button>
          ))}
        </div>
        <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
          placeholder="Buscar por obra, nombre, N° o cliente..."
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white text-gray-700 w-full sm:w-64"
          data-testid="budgets-buscador" />
        <select value={filterYear} onChange={e => setFilterYear(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white text-gray-700" data-testid="filter-year">
          <option value="">Todos los años</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white text-gray-700" data-testid="filter-month">
          <option value="">Todos los meses</option>
          {meses.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
        </select>
        <select value={filterTipo} onChange={e => setFilterTipo(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white text-gray-700" data-testid="filter-tipo">
          <option value="">Todos los tipos</option>
          <option value="obra">Obra</option>
          <option value="proyecto">Proyecto</option>
          <option value="varios">Varios</option>
        </select>
        <ClienteFilterDropdown value={filterClienteId} onChange={setFilterClienteId} clientes={clientes} />
        {filterObraId && obraFiltrada && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-violet-50 text-violet-700 border border-violet-200" data-testid="obra-filter-chip">
            Obra: {obraFiltrada.codigo} · {obraFiltrada.obra}
            <button onClick={() => { setFilterObraId(""); window.history.replaceState(null, "", "/ingenieria/presupuestos"); }}
              className="ml-1 hover:text-violet-900" data-testid="clear-obra-filter">✕</button>
          </span>
        )}
        {(filterYear || filterMonth || filterTipo || filterClienteId || busqueda) && (
          <button onClick={() => { setFilterYear(String(new Date().getFullYear())); setFilterMonth(""); setFilterTipo(""); setFilterClienteId(""); setBusqueda(""); }}
            className="text-xs text-gray-500 hover:text-gray-700 underline" data-testid="clear-filters">
            Limpiar filtros
          </button>
        )}
      </div>

      {detailLoading && <div role="status" aria-live="polite" className="flex items-center gap-3 rounded-xl bg-blue-50 border border-blue-100 px-4 py-3 text-sm text-blue-900" data-testid="budget-detail-loading">
        <span>Abriendo presupuesto…</span>
        <button type="button" onClick={cancelBudgetAction} className="underline">Cancelar</button>
      </div>}

      {/* Budget list */}
      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>
      ) : loadError ? (
        <div role="alert" className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-800">
          {loadError} <button type="button" onClick={fetchBudgets} className="underline ml-2">Reintentar</button>
        </div>
      ) : filteredBudgets.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <FileText size={44} className="mx-auto text-gray-200 mb-4" />
          <p className="text-gray-500 font-medium">No hay presupuestos</p>
          {canEdit && (
            <button onClick={openNewBudget} className="mt-4 text-[#2E6DA4] text-sm font-semibold hover:underline" data-testid="empty-new-btn">
              + Crear primer presupuesto
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredBudgets.map(b => (
            <BudgetCard
              key={b._id}
              budget={b}
              onView={openViewBudget}
              onPdf={b => prepareBudgetAction(b, full => { setPdfBudget(full); setShowPdfOptions(true); })}
              onEdit={openEditBudget}
              onDelete={canDelete ? setDeleteTarget : () => {}}
              onTransition={b => prepareBudgetAction(b, handleTransitionClick)}
              onReject={b => prepareBudgetAction(b, setRejectTarget)}
              onAttach={b => prepareBudgetAction(b, setAttachTarget)}
              onDuplicate={b => prepareBudgetAction(b, setDuplicateTarget, true)}
              onAdicional={openAdditionalBudget}
              canEdit={canEdit}
              canAdvance={canAdvance}
            />
          ))}
        </div>
      )}

      </div>

      {/* Modals */}
      <Modal open={showPriceLists} onClose={() => setShowPriceLists(false)} title="Catálogo de Precios" wide>
        <PriceListManager />
      </Modal>

      {/* Fix CC (2026-09-10) */}
      <Modal open={showBaseMadre} onClose={() => setShowBaseMadre(false)} title="Base Madre — Bocas, Mano de Obra y Catálogos" fullscreen>
        <BaseMadrePanel />
      </Modal>

      <AttachmentsModal open={!!attachTarget} onClose={() => setAttachTarget(null)} budget={attachTarget} onUpdated={fetchAll} />
      <PapeleraModal open={showPapelera} onClose={() => setShowPapelera(false)} onChanged={fetchAll} />
      <TransitionModal open={!!transitionTarget} onClose={() => setTransitionTarget(null)} onConfirm={handleTransition} budget={transitionTarget} isReject={false} />
      <TransitionModal open={!!rejectTarget} onClose={() => setRejectTarget(null)} onConfirm={handleReject} budget={rejectTarget} isReject={true} />
      <ConfirmDelete open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} nombre={deleteTarget?.nombre || ""} />
      <PdfOptionsModal open={showPdfOptions} onClose={() => { setShowPdfOptions(false); setPdfBudget(null); }} budget={pdfBudget} />
      <DuplicateModal
        open={!!duplicateTarget}
        onClose={() => setDuplicateTarget(null)}
        budget={duplicateTarget}
        clientes={clientes}
        onDuplicated={() => { setDuplicateTarget(null); fetchAll(); }}
      />
      <IniciarComputoModal
        open={showIniciarComputo}
        onClose={() => { setShowIniciarComputo(false); setComputoTarget(null); }}
        budget={computoTarget}
        onConfirm={handleIniciarComputo}
      />
    </div>
  );
}
